import React, { useState } from 'react';
import { AlertTriangle, Check } from 'lucide-react';

interface FundingRunwayProps {
    headline?: React.ReactNode;
    coveredPct?: number;
    leftLabel?: React.ReactNode;
    rightLabel?: React.ReactNode;
    depleted?: boolean;
    flag?: React.ReactNode;
    detail?: React.ReactNode;
}

/**
 * FundingRunway — the "What it means over time" beat.
 *
 * Shows how a projected cashflow deficit is funded across the plan: a plain headline, a small
 * runway bar (how far the money lasts), and a caution card ONLY when a source runs out within the
 * projection. The card matches the other module callouts: white fill + thin slate border + an amber
 * border-l-4 rail + amber icon (de-pastelled, but boxed for consistency). Calm by default; the
 * year-by-year `detail` is opt-in. Props come straight from fundingRunwayCopy() so the wording is
 * consistent across modules.
 */
export function FundingRunway({ headline, coveredPct = 100, leftLabel, rightLabel, depleted = false, flag, detail }: FundingRunwayProps) {
    const [showDetail, setShowDetail] = useState(false);
    return (
        <div>
            {headline && <p className="m-0 text-sm leading-relaxed text-slate-600">{headline}</p>}
            <div className="mt-2.5 h-2.5 overflow-hidden rounded-md bg-slate-200">
                <div
                    className="h-full rounded-md bg-gradient-to-r from-teal-400 to-brand-blue"
                    style={{ width: `${Math.max(2, Math.min(100, coveredPct))}%` }}
                />
            </div>
            <div className="mt-1.5 flex items-center justify-between text-[13px] text-slate-500">
                <span>{leftLabel}</span>
                {rightLabel && (
                    <span className={depleted ? 'font-semibold text-slate-700' : 'inline-flex items-center gap-1 font-semibold text-emerald-700'}>
                        {!depleted && <Check size={13} strokeWidth={2.5} />}
                        {rightLabel}
                    </span>
                )}
            </div>
            {flag && (
                <div className="mt-2.5 flex items-start gap-2 rounded-lg border border-slate-200 border-l-4 border-l-amber-500 bg-white px-2.5 py-2 text-[12px] leading-snug text-slate-600 shadow-card">
                    <AlertTriangle size={14} className="mt-0.5 flex-shrink-0 text-amber-500" />
                    <span>{flag}</span>
                </div>
            )}
            {detail && (
                <>
                    <button
                        type="button"
                        onClick={() => setShowDetail((v) => !v)}
                        className="mt-2.5 text-[12px] font-semibold text-brand-blue hover:underline"
                    >
                        {showDetail ? 'Hide the year-by-year' : 'Show the year-by-year ›'}
                    </button>
                    {showDetail && <div className="mt-2">{detail}</div>}
                </>
            )}
        </div>
    );
}

export default FundingRunway;
